import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { RealtimeChannel } from '@supabase/supabase-js';
interface UseProStatusListenerOptions {
  onProActivated?: () => void;
  onScansCountUpdated?: (newCount: number) => void;
  onError?: (error: Error) => void;
  enableLogging?: boolean;
}
export function useProStatusListener(options?: UseProStatusListenerOptions) {
  const {
    user
  } = useAuth();
  const [isPro, setIsPro] = useState(false);
  const [scansCount, setScansCount] = useState(0);
  const [justBecamePro, setJustBecamePro] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');
  const [error, setError] = useState<Error | null>(null);
  useEffect(() => {
    if (!user?.id) {
      if (options?.enableLogging) {
        console.log('[ProStatusListener] No user ID, skipping subscription');
      }
      return;
    }
    let channel: RealtimeChannel | null = null;
    let retryTimeout: NodeJS.Timeout | null = null;
    let retryCount = 0;
    const maxRetries = 3;
    const setupSubscription = () => {
      try {
        setConnectionStatus('connecting');
        if (options?.enableLogging) {
          console.log('[ProStatusListener] Setting up Realtime subscription for user:', user.id);
        }
        // Create channel with error handling
        channel = supabase.channel(`profile-changes-${user.id}`, {
          config: {
            broadcast: {
              self: false
            },
            presence: {
              key: ''
            }
          }
        }).on('postgres_changes', {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${user.id}`
        }, payload => {
          if (options?.enableLogging) {
            console.log('[ProStatusListener] Received update:', payload);
          }
          const newRecord = payload.new as {
            is_pro?: boolean;
            scan_credits?: number;
          };
          // Check if is_pro column changed to true
          if (newRecord.is_pro === true && !isPro) {
            if (options?.enableLogging) {
              console.log('[ProStatusListener] User became Pro!');
            }
            setIsPro(true);
            setJustBecamePro(true);
            setError(null);
            // Trigger callback if provided
            if (options?.onProActivated) {
              options.onProActivated();
            }
          }
          // Check if scan_credits was updated (for Basic plan purchases)
          if (newRecord.scan_credits !== undefined && newRecord.scan_credits !== scansCount) {
            if (options?.enableLogging) {
              console.log('[ProStatusListener] Scan credits updated:', newRecord.scan_credits);
            }
            setScansCount(newRecord.scan_credits);
            setError(null);
            // Trigger callback if provided
            if (options?.onScansCountUpdated) {
              options.onScansCountUpdated(newRecord.scan_credits);
            }
          }
        }).subscribe((status, err) => {
          if (options?.enableLogging) {
            console.log('[ProStatusListener] Subscription status:', status, err);
          }
          if (status === 'SUBSCRIBED') {
            setConnectionStatus('connected');
            setError(null);
            retryCount = 0;
          } else if (status === 'CHANNEL_ERROR') {
            setConnectionStatus('error');
            const error = new Error(err?.message || 'Channel subscription error');
            setError(error);
            if (options?.onError) {
              options.onError(error);
            }
            // Retry logic with exponential backoff
            if (retryCount < maxRetries) {
              const delay = Math.min(1000 * Math.pow(2, retryCount), 10000);
              if (options?.enableLogging) {
                console.log(`[ProStatusListener] Retrying in ${delay}ms (attempt ${retryCount + 1}/${maxRetries})`);
              }
              retryTimeout = setTimeout(() => {
                retryCount++;
                if (channel) {
                  supabase.removeChannel(channel);
                }
                setupSubscription();
              }, delay);
            } else {
              if (options?.enableLogging) {
                console.error('[ProStatusListener] Max retries reached, giving up');
              }
            }
          } else if (status === 'CLOSED') {
            setConnectionStatus('disconnected');
          }
        });
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Unknown subscription error');
        setError(error);
        setConnectionStatus('error');
        if (options?.enableLogging) {
          console.error('[ProStatusListener] Setup error:', error);
        }
        if (options?.onError) {
          options.onError(error);
        }
      }
    };
    setupSubscription();
    // Cleanup function
    return () => {
      if (options?.enableLogging) {
        console.log('[ProStatusListener] Cleaning up subscription');
      }
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [user?.id, isPro, scansCount, options]);
  return {
    isPro,
    scansCount,
    justBecamePro,
    connectionStatus,
    error,
    resetProCelebration: () => setJustBecamePro(false)
  };
}